import { useState, useMemo } from 'react';
import { PaginationState, PaginationActions, PaginationInfo } from './types';

const usePagination = (
  totalItems: number,
  defaultItemsPerPage: number = 20
): [PaginationState, PaginationActions, PaginationInfo] => {
  const [currentPage, setCurrentPage] = useState(1);
  const [itemsPerPage, setItemsPerPage] = useState(defaultItemsPerPage);

  const paginationInfo = useMemo(() => {
    const totalPages = Math.ceil(totalItems / itemsPerPage);
    const startIndex = (currentPage - 1) * itemsPerPage;
    const endIndex = Math.min(startIndex + itemsPerPage, totalItems);
    const hasNextPage = currentPage < totalPages;
    const hasPreviousPage = currentPage > 1;

    const visiblePages: number[] = [];
    const maxVisiblePages = 5;
    let startPage = Math.max(1, currentPage - Math.floor(maxVisiblePages / 2));
    const endPage = Math.min(totalPages, startPage + maxVisiblePages - 1);

    if (endPage - startPage + 1 < maxVisiblePages) {
      startPage = Math.max(1, endPage - maxVisiblePages + 1);
    }

    for (let i = startPage; i <= endPage; i++) {
      visiblePages.push(i);
    }

    return {
      totalPages,
      startIndex,
      endIndex,
      hasNextPage,
      hasPreviousPage,
      visiblePages,
    };
  }, [currentPage, itemsPerPage, totalItems]);

  const actions: PaginationActions = {
    setCurrentPage: (page: number) => {
      if (page >= 1 && page <= paginationInfo.totalPages) {
        setCurrentPage(page);
      }
    },
    setItemsPerPage: (newItemsPerPage: number) => {
      setItemsPerPage(newItemsPerPage);
      setCurrentPage(1);
    },
    nextPage: () => {
      if (paginationInfo.hasNextPage) {
        setCurrentPage(currentPage + 1);
      }
    },
    previousPage: () => {
      if (paginationInfo.hasPreviousPage) {
        setCurrentPage(currentPage - 1);
      }
    },
    goToPage: (page: number) => {
      if (page >= 1 && page <= paginationInfo.totalPages) {
        setCurrentPage(page);
      }
    },
  };

  const state: PaginationState = {
    currentPage,
    itemsPerPage,
    totalItems,
  };

  return [state, actions, paginationInfo];
};

export default usePagination;